import type { Subscription } from '@orgus/protocol';
import { formatUsd } from '@orgus/protocol';
import { useEffect, useState } from 'react';
import { Button, Dialog, ErrorState, Input, Segmented, Select, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useProjects } from '../../lib/store.ts';
import { projectOptions } from './EntryDialog.tsx';
import { dayOf } from './model.ts';
import { saveSubscription } from './useFinance.ts';

/** Add or change a seat or subscription; it is amortised per day from its start. */
export function SubscriptionDialog({ open, onOpenChange, sub }: { open: boolean; onOpenChange(v: boolean): void; sub: Subscription | null }) {
  const projects = useProjects();
  const [vendor, setVendor] = useState('');
  const [plan, setPlan] = useState('');
  const [seats, setSeats] = useState('1');
  const [price, setPrice] = useState('');
  const [every, setEvery] = useState<'month' | 'year'>('month');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [projectId, setProjectId] = useState('');
  const [kind, setKind] = useState<'agent' | 'other'>('other');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);

  useEffect(() => {
    if (!open) return;
    setVendor(sub?.vendor ?? '');
    setPlan(sub?.plan ?? '');
    setSeats(String(sub?.seats ?? 1));
    setPrice(sub ? String(sub.pricePerSeat) : '');
    setEvery(sub?.every ?? 'month');
    setStart(sub?.start ?? dayOf(new Date()));
    setEnd(sub?.end ?? '');
    setProjectId(sub?.projectId ?? '');
    setKind(sub?.agentSeat ? 'agent' : 'other');
    setError(null);
  }, [open, sub]);

  const seatsNum = Number(seats);
  const priceNum = Number(price.replace(/[$,\s]/g, ''));
  const valid = vendor.trim() && plan.trim() && Number.isInteger(seatsNum) && seatsNum >= 1 && Number.isFinite(priceNum) && priceNum >= 0 && start && (!end || end >= start);
  const monthly = valid ? (seatsNum * priceNum) / (every === 'year' ? 12 : 1) : 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveSubscription({ vendor, plan, seats: seatsNum, pricePerSeat: priceNum, every, start, end: end || null, projectId: projectId || null, agentSeat: kind === 'agent' }, sub?.id);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'That subscription was not saved.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      dismissible={!busy}
      title={sub ? `Edit ${sub.vendor} ${sub.plan}` : 'Add a seat or subscription'}
      description="The cost is spread over each day of the billing period."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={() => void submit()}>
            {sub ? 'Save subscription' : 'Add subscription'}
          </Button>
        </>
      }
    >
      <form
        className="grid grid-cols-2 gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) void submit();
        }}
      >
        <div className="col-span-2">
          <Segmented<'agent' | 'other'>
            label="Kind"
            size="md"
            options={[
              { value: 'agent', label: 'Agent seats (Claude Pro, Max, Team)' },
              { value: 'other', label: 'Other software' },
            ]}
            value={kind}
            onChange={setKind}
          />
        </div>
        <Input label="Vendor" placeholder="Anthropic" value={vendor} onChange={(e) => setVendor(e.target.value)} maxLength={200} />
        <Input label="Plan" placeholder="Claude Max" value={plan} onChange={(e) => setPlan(e.target.value)} maxLength={200} />
        <Input label="Seats" inputMode="numeric" value={seats} onChange={(e) => setSeats(e.target.value)} error={seats && !(Number.isInteger(seatsNum) && seatsNum >= 1) ? 'A whole number, at least 1.' : undefined} />
        <Input label="Price per seat (USD)" inputMode="decimal" placeholder="200" value={price} onChange={(e) => setPrice(e.target.value)} />
        <div className="col-span-2 flex items-center gap-3">
          <Segmented<'month' | 'year'>
            label="Billed"
            size="md"
            options={[
              { value: 'month', label: 'Every month' },
              { value: 'year', label: 'Every year' },
            ]}
            value={every}
            onChange={setEvery}
          />
          <span className="text-sm text-fg-muted num">{valid ? `${formatUsd(monthly)} a month` : ''}</span>
        </div>
        <Input label="First day billed" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        <Input label="Last day" type="date" hint="Leave empty while it runs" value={end} onChange={(e) => setEnd(e.target.value)} error={end && start && end < start ? 'The last day is before the first.' : undefined} />
        <Select label="Project" wrapperClassName="col-span-2" options={projectOptions(projects)} value={projectId} onValueChange={setProjectId} />
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
      {error && <ErrorState error={error} variant="inline" className="mt-3" />}
    </Dialog>
  );
}
